import React, { useEffect, useState } from 'react';

import type { UserEditDTO } from '../../../services/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

import Card from '../../../components/common/Card';
import UserForm from '../../../components/forms/UserForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import { useUserApi } from '../../../hooks/useUserApi';

const UserEdit: React.FC = () => {
  const [user, setUser] = useState<UserEditDTO>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getById, uploadProfilePicture, edit } = useUserApi();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Users', href: '/admin/users' },
    { label: 'Edit', href: '#' },
  ];

  useEffect(() => {
    const fetchUser = async (): Promise<void> => {
      try {
        if (id) {
          const response = await getById(id);
          if (response && response.success && response.data) {
            setUser(response.data as UserEditDTO);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleSubmit = async (data: UserEditDTO): Promise<void> => {
    setIsSubmitting(true);
    try {
      if (profilePicture) {
        // Call the method to upload the logo image
        const uploadResponse = await uploadProfilePicture(
          profilePicture.name,
          profilePicture,
        );

        if (uploadResponse && uploadResponse.success) {
          data.profilePictureFileId = uploadResponse.data;
        }
      }
      if (id) {
        const response = await edit(id, data);
        if (response && response.success && response.data) {
          localStorage.removeItem(`profilePic_${response.data?.id}`);
          navigate('/admin/users');
        }
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/users"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      {isLoading ? (
        <p>Loading...</p>
      ) : (
          <UserForm
            initialValues={user!}
            isSubmitting={isSubmitting}
            onSave={handleSubmit}
            setProfilePicture={setProfilePicture}
            onCancel={() => navigate('/admin/users')}
            isCreating={false}
          />
      )}
    </AdminLayout>
  );
};

export default UserEdit;
